import type { Bundle } from "fhir/r5";

import { endpoints } from "@/configs/Settings";
import { HTTPResponseStatus } from "@/utils/enum";
import { useAuth } from "@/utils/useAuth";
import { useAxios } from "@/utils/useAxios";

import {
  buildWearableObservationSearchRequests,
  buildWearableObservationSearchUrl,
} from "./wearableObservationSearchUrls";

/** Payload POST do EHRRunner (MHD) — `Bundle` sem `type` (profile PatientSummary). */
type MhdPatientSummaryRequestBundle = {
  resourceType: "Bundle";
  meta: {
    profile: string[];
  };
  entry: Array<{
    request: {
      method: "GET";
      url: string;
    };
  }>;
};

/** Limite de entries GET por Bundle batch no EHRRunner (MHD). */
const MHD_BATCH_CHUNK_SIZE = 50;

const MHD_BATCH_TIMEOUT_MS = 60000;

function chunkArray<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

/**
 * Bundle POST no EHRRunner (MHD) — mesmo formato de `AuxiliarData.PatientSummary`:
 * só `resourceType: "Bundle"` + `meta.profile`, **sem** `type: "batch"`.
 */
function buildMhdObservationGetBatchBundle(
  getUrls: string[],
): MhdPatientSummaryRequestBundle {
  return {
    resourceType: "Bundle",
    meta: {
      profile: ["http://ehrrunner.com/fhir/StructureDefinition/PatientSummary"],
    },
    entry: getUrls.map((url) => ({
      request: {
        method: "GET",
        url,
      },
    })),
  };
}

/** Extrai entries de Observation da resposta do PatientSummary no MHD. */
function extractObservationBundleEntries(batchResponse: Bundle): any[] {
  const seen = new Set<string>();
  const entries: any[] = [];

  const pushEntry = (observationEntry: any) => {
    const dedupeKey = String(
      observationEntry?.resource?.id || observationEntry?.fullUrl || "",
    );
    if (dedupeKey && seen.has(dedupeKey)) return;
    if (dedupeKey) seen.add(dedupeKey);
    entries.push(observationEntry);
  };

  for (const item of batchResponse.entry ?? []) {
    const inner = item.resource as Bundle | { resourceType?: string } | undefined;
    if (!inner) continue;

    // Resposta padrão PatientSummary: entry.resource é um searchset Bundle.
    if (inner.resourceType === "Bundle") {
      for (const observationEntry of (inner as Bundle).entry ?? []) {
        if (observationEntry?.resource?.resourceType === "Observation") {
          pushEntry(observationEntry);
        }
      }
      continue;
    }

    // Fallback: Observation direta na entry (formato alternativo).
    if (inner.resourceType === "Observation") {
      pushEntry(item);
    }
  }

  return entries;
}

async function postMhdObservationBatchChunk(
  getUrls: string[],
): Promise<any[]> {
  if (!getUrls.length) return [];

  const subjectId = await useAuth.getSubjectId();
  const organizationId = await useAuth.getOrganizationId();

  const headers = {
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/fhir+json",
    Accept: "application/fhir+json",
    "x-user-token": await useAuth.getXUserToken(),
    ...(subjectId && { "subject-id": subjectId }),
    ...(organizationId && { "organization-id": organizationId }),
    purposeofuse: "Atendimento",
  };

  const body = buildMhdObservationGetBatchBundle(getUrls);
  const response = await useAxios.post(`${endpoints.MHD}`, body, {
    headers,
    timeout: MHD_BATCH_TIMEOUT_MS,
  });

  if (response.status !== HTTPResponseStatus.Success) {
    throw new Error(`GetWearableObservationsMhdBatch: ${response.status}`);
  }

  return extractObservationBundleEntries(response.data as Bundle);
}

/**
 * Busca Observations wearable via **POST Bundle** no EHRRunner (MHD).
 * Formato igual ao `BundlePatientSummary`: entries com `GET Observation?subject=Patient/...`.
 */
export async function GetWearableObservationsMhdBatch(params: {
  patientId: string;
  periodStartKey: string;
  periodEndKey: string;
  rangeDays: number;
}): Promise<any[]> {
  const patientId = params.patientId?.trim();
  if (!patientId) return [];

  const searchRequests = buildWearableObservationSearchRequests(params);
  const getUrls = searchRequests.map((req) =>
    buildWearableObservationSearchUrl(patientId, req),
  );

  const urlChunks = chunkArray(getUrls, MHD_BATCH_CHUNK_SIZE);
  const allEntries: any[] = [];
  const seen = new Set<string>();

  for (const chunk of urlChunks) {
    try {
      const entries = await postMhdObservationBatchChunk(chunk);
      for (const entry of entries) {
        const dedupeKey = String(entry?.resource?.id || entry?.fullUrl || "");
        if (dedupeKey && seen.has(dedupeKey)) continue;
        if (dedupeKey) seen.add(dedupeKey);
        allEntries.push(entry);
      }
    } catch (error) {
      console.error("GetWearableObservationsMhdBatch chunk failed", error);
    }
  }

  return allEntries;
}
